/**
 * "Matching observations (N matches)": the newest that match, newest first, 20
 * at a time ("Load more" appends the next 20), each with a radio to pick it as
 * the sample; the pick stays as pages load. The radios are one group,
 * so the arrow keys move the pick; Enter picks the focused row too.
 */

import { useId } from "react";
import { Button } from "@/components/ui/button";
import { QueryState, errorText } from "@/components/ui/states";
import { formatDateTime, formatLatency, plural } from "@/lib/format";
import { useMatchCount, useTracePages } from "./hooks";
import type { TraceQuery } from "./trace-query";
import type { TraceRef } from "./types";

/** What a pick is kept by: the observation, or the trace for its root. */
export const refKey = (r: Pick<TraceRef, "id" | "observation_id">) => r.observation_id ?? r.id;

export function ObservationTable({
  sourceId,
  query,
  selected,
  onSelect,
}: {
  sourceId: string;
  query: TraceQuery;
  /** `refKey` of the picked row, or "". */
  selected: string;
  onSelect: (ref: TraceRef) => void;
}) {
  const group = useId();
  const count = useMatchCount(sourceId, query);
  const pages = useTracePages(sourceId, query);
  const rows = pages.data?.pages.flatMap((p) => p.items) ?? [];
  // A failed "Load more" keeps the rows shown; only a failed first page is an error.
  const first = { isPending: pages.isPending, isError: pages.isError && !pages.data, error: pages.error };
  const ready = !!sourceId;
  const matched = count.data ? ` (${plural(count.data.matched, "match", "matches")})` : "";

  return (
    <section aria-label="Matching observations" className="flex flex-col gap-1.5">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-xs font-semibold">
          Matching observations<span className="font-normal text-muted">{matched}</span>
        </h3>
        <span className="text-xs text-muted">
          {ready
            ? "Select one to test the evaluator and check the mapping. Last 92 days."
            : "Pick a source to see matching observations."}
        </span>
      </div>
      {ready && (
        <QueryState query={first} what="the observations">
          {rows.length === 0 ? (
            <p className="text-xs text-muted">No observations match these filters in the last 92 days.</p>
          ) : (
            <div className="max-h-72 overflow-auto rounded-lg border border-line-soft">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-well text-left text-faint">
                  <tr>
                    <th className="w-8 px-2 py-1.5">
                      <span className="sr-only">Pick</span>
                    </th>
                    <th className="px-2 py-1.5 font-semibold">Start time</th>
                    <th className="px-2 py-1.5 font-semibold">Type</th>
                    <th className="px-2 py-1.5 font-semibold">Name</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Latency</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const key = refKey(r);
                    const on = key === selected;
                    const when = formatDateTime(r.timestamp);
                    return (
                      <tr
                        key={key}
                        className={`cursor-pointer border-t border-line-soft ${on ? "bg-selected" : "hover:bg-hover"}`}
                        onClick={() => onSelect(r)}
                      >
                        <td className="px-2 py-1.5">
                          <input
                            type="radio"
                            name={group}
                            className="size-3.5 accent-ink"
                            aria-label={`${r.name ?? r.type ?? "Observation"} at ${when}`}
                            checked={on}
                            onChange={() => onSelect(r)}
                            onKeyDown={(e) => e.key === "Enter" && onSelect(r)}
                          />
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">{when}</td>
                        <td className="px-2 py-1.5">{r.type ?? "—"}</td>
                        <td className="max-w-[220px] truncate px-2 py-1.5" title={r.name ?? undefined}>
                          {r.name ?? "—"}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{formatLatency(r.latency_ms)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {pages.isFetchNextPageError ? (
            <p role="alert" className="flex flex-wrap items-center gap-x-1 text-xs text-err">
              Could not load more. {errorText(pages.error, "The server did not answer.")}
              <Button variant="ghost" size="sm" onClick={() => pages.fetchNextPage()}>
                Try again
              </Button>
            </p>
          ) : (
            pages.hasNextPage && (
              <Button
                variant="ghost"
                size="sm"
                className="self-start text-muted"
                disabled={pages.isFetchingNextPage}
                onClick={() => pages.fetchNextPage()}
              >
                {pages.isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            )
          )}
        </QueryState>
      )}
    </section>
  );
}
